import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router';
import HoldingsTable from '@/modules/portfolio/tables/holdingsTable';
import HoldingsHistoricalTable from '@/modules/portfolio/tables/holdingsHistoricalTable';
import { useControlledDates } from '@/hooks/useControlledDates';
import { StandardWidgetProps } from '@/models/WidgetModels';
import AsOfDatePicker from '@/components/date/AsOfDatePicker';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import { ViewMode } from '@/constants/enum';
import { Box } from '@mui/material';
import ViewModeBar from '@/components/common/ViewModeBar';
import DateRange from '@/components/date/DateRange';
import WidgetHeader from '@/components/common/WidgetHeader';
import { useLocalization } from '@/hooks/useLocalization';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';
import { useGetAccountTagsQuery } from '@/store/accountsApi';
import { usePageDefaults } from '@/hooks/usePageDefaults';
import { useExplicitPageState } from '@/hooks/useExplicitPageState';
import { GroupBySelect, GroupOption } from '@/components/common/GroupBySelect';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import { dashboardMobileWidthValue } from '@/constants';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { ContentContainer, ContentContainerVariant } from '../contentContainer';

interface PortfolioHoldingsProps extends StandardWidgetProps {
  startDate?: string;
  endDate?: string;
  showDateRange?: boolean;
  outletSetShowDateRange?: (show: boolean) => void;
  excludedAccounts?: number[];
  excludedCategories?: number[];
  excludedAccountTypes?: number[];
  excludedInstitutions?: number[];
  container?: ContentContainerVariant;
}

interface HoldingsGroupOption extends GroupOption {
  isCustom?: boolean;
}

const { startDate: initialPageStartDate, endDate: initialPageEndDate } =
  getDateRangeFromFilter(DataRangeFilterEnum.OpenYear);

const PortfolioHoldings = ({
  isWidget = false,
  startDate: propStartDate,
  endDate,
  onDataLoaded,
  excludedAccounts,
  excludedAccountTypes,
  excludedCategories,
  title,
  container = 'page',
}: PortfolioHoldingsProps) => {
  const outletContext = useOutletContext<{
    startDate?: string;
    endDate?: string;
    outletSetShowDateRange?: (show: boolean) => void;
    excludedAccounts?: number[];
    excludedCategories?: number[];
    excludedAccountTypes?: number[];
    excludedInstitutions?: number[];
  } | null>();
  const outletStartDate = outletContext?.startDate;
  const outletEndDate = outletContext?.endDate;
  const outletSetShowDateRange = outletContext?.outletSetShowDateRange;
  const outletExcludedAccounts = outletContext?.excludedAccounts;
  const outletExcludedCategories = outletContext?.excludedCategories;
  const outletExcludedAccountTypes = outletContext?.excludedAccountTypes;
  const outletExcludedInstitutions = outletContext?.excludedInstitutions;
  const { ref: widgetWidthRef, isBelow: isWidgetBelowNarrowDateField } =
    useWidgetWidthIsBelow(isWidget, dashboardMobileWidthValue);
  const widgetNarrowDateField = isWidget && isWidgetBelowNarrowDateField;

  const effectiveExclusions = useEffectiveExclusions({
    excludedAccounts: excludedAccounts || outletExcludedAccounts,
    excludedCategories: excludedCategories || outletExcludedCategories,
    excludedAccountTypes: excludedAccountTypes || outletExcludedAccountTypes,
    excludedInstitutions: outletExcludedInstitutions,
  });
  const [showDateRange, setShowDateRange] = useState(false);
  const { t } = useLocalization();
  const widgetTitle = title ?? t('holdings');
  const { data: accountTags } = useGetAccountTagsQuery({});

  // Get firm-configured default grouping from menu settings
  const { defaultGroup: firmDefaultGroup, defaultView: firmDefaultView } =
    usePageDefaults('/Portfolio/Holdings', 'holdings');

  // View mode options for persistence
  const VIEW_OPTIONS = useMemo(() => [ViewMode.Table, ViewMode.Historical], []);

  const GROUP_OPTIONS: HoldingsGroupOption[] = useMemo(
    () => [
      { value: 'none', label: t('none') },
      { value: 'account', label: t('account') },
    ],
    [t],
  );

  const userGeneratedGroups = useMemo(() => {
    if (!accountTags) return [] as HoldingsGroupOption[];
    return accountTags.map((group) => {
      const prefix = group.IsAdvisorTag ? 'advisor' : 'investor';
      return {
        value: `${prefix}:${String(group.TagCategoryId)}`,
        label: group.Name,
        isCustom: true,
      };
    });
  }, [accountTags]);

  const allGroupOptions = useMemo(
    () => [...GROUP_OPTIONS, ...userGeneratedGroups],
    [GROUP_OPTIONS, userGeneratedGroups],
  );

  // Use explicit page state for grouping and view mode
  // Changes are session-only until explicitly saved
  const {
    currentGroup: activeGroup,
    setCurrentGroup: setActiveGroup,
    currentView: viewMode,
    setCurrentView: setViewMode,
    savedGroupValue,
    hasUnsavedGroupChange,
    isUsingFirmDefaultGroup,
    saveGroupAsDefault,
    resetGroupToFirmDefault,
  } = useExplicitPageState({
    pageKey: 'Holdings',
    groupOptions: allGroupOptions,
    viewOptions: VIEW_OPTIONS,
    firmDefaultGroup,
    firmDefaultView,
    getGroupValue: (g) => g.value,
    getViewValue: (v) => v,
  });

  const {
    startDate,
    endDate: controlledEndDate,
    setStartDate,
    setEndDate,
  } = useControlledDates({
    propStartDate: outletStartDate ? outletStartDate : propStartDate,
    propEndDate: outletEndDate ? outletEndDate : endDate,
    isWidget,
    initialWidgetStartDate: initialPageStartDate,
    initialWidgetEndDate: initialPageEndDate,
  });

  // Set showDateRange to true when viewMode is Historical
  useEffect(() => {
    if (isWidget && viewMode === ViewMode.Historical) {
      setShowDateRange(true);
    } else if (viewMode === ViewMode.Historical && outletSetShowDateRange) {
      outletSetShowDateRange(true);
    } else {
      setShowDateRange(false);
      if (outletSetShowDateRange) {
        outletSetShowDateRange(false);
      }
    }
  }, [viewMode, setShowDateRange, outletSetShowDateRange, isWidget]);

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleGroupChange = (option: HoldingsGroupOption) => {
    setActiveGroup(option);
  };

  const handleViewModeChange = (newViewMode: ViewMode) => {
    setViewMode(newViewMode);
  };

  const datePickerContent = (
    <>
      {isWidget && (
        <>
          {showDateRange ? (
            <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
              <DateRange
                startDate={startDate}
                endDate={controlledEndDate}
                onChange={handleDateRangeChange}
                useWidgetNarrowIconField={widgetNarrowDateField}
              />
            </Box>
          ) : (
            <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
              <AsOfDatePicker
                date={controlledEndDate}
                onChange={setEndDate}
                label={t('asOf')}
                useWidgetNarrowIconField={widgetNarrowDateField}
              />
            </Box>
          )}
        </>
      )}
    </>
  );

  return (
    <ContentContainer
      ref={widgetWidthRef}
      sx={{
        paddingX: 3,
        paddingTop: 3,
        paddingBottom: 5,
        gap: 4,
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}
      container={container}
    >
      <WidgetHeader
        title={widgetTitle}
        rightContent={datePickerContent}
        viewMode={
          !isWidget
            ? {
                currentViewMode: viewMode,
                setViewMode: handleViewModeChange,
                viewModes: [ViewMode.Table, ViewMode.Historical],
              }
            : undefined
        }
        leftContent={
          <GroupBySelect
            label={t('groupBy')}
            options={allGroupOptions}
            activeOption={activeGroup}
            onOptionChange={handleGroupChange}
            savedGroupValue={savedGroupValue}
            firmDefaultGroup={firmDefaultGroup}
            hasUnsavedGroupChange={hasUnsavedGroupChange}
            isUsingFirmDefaultGroup={isUsingFirmDefaultGroup}
            onSaveAsDefault={saveGroupAsDefault}
            onResetToFirmDefault={resetGroupToFirmDefault}
            minWidth={120}
            sx={{ marginTop: 2 }}
          />
        }
      />

      {isWidget && (
        <ViewModeBar
          viewModes={[ViewMode.Table, ViewMode.Historical]}
          currentViewMode={viewMode}
          setViewMode={handleViewModeChange}
        />
      )}

      {viewMode === ViewMode.Table && (
        <Box
          sx={(theme) => ({
            [theme.breakpoints.down(800)]: {
              '& .MuiDataGrid-cell': {
                width: '100% !important',
                minWidth: '100% !important',
              },
            },
          })}
        >
          <HoldingsTable
            endDate={controlledEndDate || ''}
            isWidget={isWidget}
            groupBy={activeGroup.value}
            onDataLoaded={onDataLoaded}
            excludedAccounts={effectiveExclusions.excludedAccounts}
            excludedAccountTypes={effectiveExclusions.excludedAccountTypes}
            excludedCategories={effectiveExclusions.excludedCategories}
          />
        </Box>
      )}
      {viewMode === ViewMode.Historical && (
        <HoldingsHistoricalTable
          startDate={startDate}
          endDate={controlledEndDate || ''}
          isWidget={isWidget}
          groupBy={activeGroup.value}
          onDataLoaded={onDataLoaded}
          excludedAccounts={effectiveExclusions.excludedAccounts}
          excludedAccountTypes={effectiveExclusions.excludedAccountTypes}
          excludedCategories={effectiveExclusions.excludedCategories}
        />
      )}
    </ContentContainer>
  );
};

export default PortfolioHoldings;
